// ============================================
// PRIVLUXE - DEBUG LOGGER
// ============================================
// Captures console logs for mobile debugging
// View logs at /debug-logs (admin only)
// ============================================

const MAX_LOGS = 500;
const LOG_KEY = 'privluxe_debug_logs';

// Store logs in memory and localStorage
let logs = [];

// Load existing logs from localStorage
try {
  const stored = localStorage.getItem(LOG_KEY);
  if (stored) {
    logs = JSON.parse(stored);
  }
} catch (e) {
  logs = [];
}

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', { 
    hour12: false, 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  }) + '.' + String(now.getMilliseconds()).padStart(3, '0');
};

const addLog = (type, args) => {
  const entry = {
    time: formatTime(),
    timestamp: Date.now(),
    type,
    message: args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ')
  };
  
  logs.push(entry);
  
  // Keep only last MAX_LOGS
  if (logs.length > MAX_LOGS) {
    logs = logs.slice(-MAX_LOGS);
  }
  
  // Save to localStorage
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(logs));
  } catch (e) {
    // Storage full, clear old logs
    logs = logs.slice(-100);
    localStorage.setItem(LOG_KEY, JSON.stringify(logs));
  }
};

// Store original console methods
const originalConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
};

// Override console methods
export const initLogger = () => {
  console.log = (...args) => {
    addLog('log', args);
    originalConsole.log(...args);
  };
  
  console.error = (...args) => {
    addLog('error', args);
    originalConsole.error(...args);
  };
  
  console.warn = (...args) => {
    addLog('warn', args);
    originalConsole.warn(...args);
  };
  
  console.info = (...args) => {
    addLog('info', args);
    originalConsole.info(...args);
  };
  
  console.debug = (...args) => {
    addLog('debug', args);
    originalConsole.debug(...args);
  };
  
  // Capture unhandled errors
  window.onerror = (message, source, lineno, colno, error) => {
    addLog('error', [`UNCAUGHT: ${message}`, `at ${source}:${lineno}:${colno}`, error?.stack || '']);
    return false;
  };
  
  // Capture unhandled promise rejections
  window.onunhandledrejection = (event) => {
    addLog('error', ['UNHANDLED PROMISE:', event.reason?.message || event.reason, event.reason?.stack || '']);
  };
  
  // Log network errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
    const method = args[1]?.method || 'GET';
    
    try {
      const response = await originalFetch(...args);
      if (!response.ok) {
        addLog('warn', [`FETCH ${method} ${url} - ${response.status} ${response.statusText}`]);
      }
      return response;
    } catch (error) {
      addLog('error', [`FETCH FAILED ${method} ${url}:`, error.message]);
      throw error;
    }
  };
  
  console.log('[Logger] Debug logger initialized');
};

// Get all logs
export const getLogs = () => logs;

// Clear all logs
export const clearLogs = () => {
  logs = [];
  localStorage.removeItem(LOG_KEY);
};

// Get logs as downloadable text
export const getLogsAsText = () => {
  return logs.map(l => `[${l.time}] [${l.type.toUpperCase()}] ${l.message}`).join('\n');
};

// Export for use in components
export default {
  initLogger,
  getLogs,
  clearLogs,
  getLogsAsText
};
